// src/admin/pages/return/ReturnManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Pagination,
  InputAdornment,
  Collapse,
  useTheme,
  useMediaQuery,
  Divider,
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Backdrop,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  TaskAlt as TaskAltIcon,
  Inventory as InventoryIcon,
  AssignmentReturn as AssignmentReturnIcon,
  Receipt as ReceiptIcon,
  Email as EmailIcon,
  Notes as NotesIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
  HourglassEmpty as HourglassEmptyIcon,
  Person as PersonIcon,
  Payments as PaymentsIcon,
  Save as SaveIcon,
  Edit as EditIcon,
  LocalOffer as LocalOfferIcon,
  InfoOutlined as InfoOutlinedIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  ReturnRequest,
  ReturnStatus,
  RefundStatus,
  RefundMethod,
  RETURN_REASON_LABELS,
  RETURN_STATUS_LABELS,
  REFUND_STATUS_LABELS,
  REFUND_METHOD_LABELS,
  ReturnRequestService,
} from './returnRequestService';
import { OrderService } from '../order/orderService';
import { ProductService } from '../product/productService';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import { formatNumber } from '../../../util/Formatcurrency';
import {
  useReturnRequestsPaginated,
  useReturnStats,
  useReturnMutations,
} from '../../../hooks/useReturnRequests';
import ToastNotification from '../common/Toastnotification';
import { useAuth } from '../../../contexts/AuthContext';

const REQUESTS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const statusColor = (status: ReturnStatus): string => {
  switch (status) {
    case 'pending': return 'warning';
    case 'approved': return 'info';
    case 'rejected': return 'error';
    case 'completed': return 'success';
    default: return 'default';
  }
};

const statusIcon = (status: ReturnStatus) => {
  switch (status) {
    case 'pending': return <HourglassEmptyIcon fontSize="small" />;
    case 'approved': return <CheckCircleIcon fontSize="small" />;
    case 'rejected': return <CancelIcon fontSize="small" />;
    case 'completed': return <TaskAltIcon fontSize="small" />;
    default: return null;
  }
};

const refundStatusColor = (rs: RefundStatus | undefined): string => {
  if (!rs) return 'default';
  switch (rs) {
    case 'refunded': return 'success';
    case 'partially_refunded': return 'secondary';
    default: return 'warning';
  }
};

const formatDate = (date?: Date): string => {
  if (!date) return '—';
  return date.toLocaleDateString('en-US', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();

  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: rect.top, left: rect.left,
        width: rect.width, height: rect.height,
        backgroundColor: overlayBg,
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading…</Typography>
      </Box>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: React.ReactNode; icon?: React.ReactNode }> = ({ label, value, icon }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 0.75 }}>
    {icon && (
      <Box sx={{ color: 'text.disabled', mt: 0.25, flexShrink: 0, display: 'flex' }}>{icon}</Box>
    )}
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3, mb: 0.25 }}>
        {label}
      </Typography>
      <Typography variant="body2" color="text.primary" fontWeight={500}>{value}</Typography>
    </Box>
  </Box>
);

const DrawerSectionHeader: React.FC<{ icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({ icon, title, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
    <Stack direction="row" alignItems="center" spacing={1}>
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>{icon}</Box>
      <Typography variant="subtitle2" fontWeight={700} color="text.primary">{title}</Typography>
    </Stack>
    {action}
  </Box>
);

type ActionType = 'approve' | 'reject' | 'complete' | 'notes' | null;

const getActionLabel = (action: ActionType): string => {
  switch (action) {
    case 'approve': return 'Approving Return…';
    case 'reject': return 'Rejecting Return…';
    case 'complete': return 'Completing Return…';
    case 'notes': return 'Saving Notes…';
    default: return 'Processing…';
  }
};

const ReturnManagement: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDark = theme.palette.mode === 'dark';

  const { companyProfile } = useCompany();
  const companyCurrency = companyProfile?.currency || 'USD';
  const currencySymbol = getCurrencySymbol(companyCurrency);
  const formatCurrency = (amount: number) => `${currencySymbol} ${formatNumber(amount, 2)}`;

  // ── UI state ──────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

  // ── Pagination & filter state ─────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [reasonFilter, setReasonFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  // ── Drawer state ──────────────────────────────────────────────────────────
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<ReturnRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState<ActionType>(null);

  // Refund form state
  const [refundStatus, setRefundStatus] = useState<RefundStatus>('pending');
  const [refundedAmount, setRefundedAmount] = useState('');
  const [refundMethod, setRefundMethod] = useState<RefundMethod>('original_payment');
  const [refundTransactionId, setRefundTransactionId] = useState('');
  const [refundDate, setRefundDate] = useState('');
  const [refundNotes, setRefundNotes] = useState('');
  const [refundLoading, setRefundLoading] = useState(false);
  const [refundEditMode, setRefundEditMode] = useState(false);

  const { hasPermission, isSuperAdmin } = useAuth();

  const canView = isSuperAdmin || hasPermission('orders.return.view');
  const canManage = isSuperAdmin || hasPermission('orders.return.manage'); // approve, reject, complete, notes, refund edit, delete

  // Stats loading
  const [statsLoading, setStatsLoading] = useState(false);

  // Reject dialog
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectDialogLoading, setRejectDialogLoading] = useState(false);

  // ── Delete state ──────────────────────────────────────────────────────────
  const [deleteTarget, setDeleteTarget] = useState<ReturnRequest | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
  const [deleteLoadingId, setDeleteLoadingId] = useState<string>('');

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // ── React Query hooks ─────────────────────────────────────────────────────
  const {
    data: requestsData,
    isLoading: requestsLoading,
    isFetching: requestsIsFetching,
    isError: requestsIsError,
    error: requestsErrorObj,
  } = useReturnRequestsPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    reason: reasonFilter,
    pageSize: REQUESTS_PER_PAGE,
  });

  const { data: statsData } = useReturnStats();
  const { approve, reject, complete, saveNotes, saveRefund, remove } = useReturnMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const requests = requestsData?.requests ?? [];
  const totalRequests = requestsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalRequests / REQUESTS_PER_PAGE);
  const allStats = statsData ?? { pending: 0, approved: 0, rejected: 0, completed: 0 };
  const loading = requestsLoading || requestsIsFetching;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: ToastState['severity']) =>
    setToast({ open: true, message, severity });
  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all' || reasonFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all', reasonFilter !== 'all'].filter(Boolean).length;
  const refundAmountEditable = refundEditMode && refundStatus !== 'pending';

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Filters ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const handleReasonFilterChange = (value: string) => {
    setReasonFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    setReasonFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const handleRefresh = () => {
    resetToFirstPage();
    showToast('Refreshed', 'info');
  };

  // ── Drawer ─────────────────────────────────────────────────────────────────
  const openDrawer = (request: ReturnRequest) => {
    setSelectedRequest(request);
    setAdminNotes(request.adminNotes || '');
    setRefundStatus(request.refundStatus || 'pending');
    setRefundedAmount(
      request.refundedAmount !== undefined
        ? String(request.refundedAmount)
        : String(request.refundAmount)
    );
    setRefundMethod(request.refundMethod || 'original_payment');
    setRefundTransactionId(request.refundTransactionId || '');
    setRefundDate(
      request.refundDate
        ? request.refundDate.toISOString().split('T')[0]
        : ''
    );
    setRefundNotes(request.refundNotes || '');
    setRefundEditMode(false);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRequest(null);
    setAdminNotes('');
    setRefundStatus('pending');
    setRefundedAmount('');
    setRefundMethod('original_payment');
    setRefundTransactionId('');
    setRefundDate('');
    setRefundNotes('');
    setRefundEditMode(false);
    setCurrentAction(null);
  };

  // ── Actions ────────────────────────────────────────────────────────────────
  const handleApprove = async () => {
    if (!selectedRequest?.id) return;
    try {
      setCurrentAction('approve');
      setActionLoading(true);
      await approve.mutateAsync({ requestId: selectedRequest.id, adminNotes });
      showToast('Return request approved', 'success');
      closeDrawer();
      resetToFirstPage();
    } catch (err: any) {
      showToast(`Failed to approve: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setActionLoading(false);
      setCurrentAction(null);
    }
  };

  const handleReject = () => {
    if (!selectedRequest?.id) return;
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!selectedRequest?.id) return;
    try {
      setRejectDialogLoading(true);
      setCurrentAction('reject');
      setActionLoading(true);
      await reject.mutateAsync({ requestId: selectedRequest.id, adminNotes });
      setRejectDialogOpen(false);
      showToast('Return request rejected', 'success');
      closeDrawer();
      resetToFirstPage();
    } catch (err: any) {
      setRejectDialogOpen(false);
      showToast(`Failed to reject: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setRejectDialogLoading(false);
      setActionLoading(false);
      setCurrentAction(null);
    }
  };

  if (!canView) {
    return (
      <Box sx={{ p: 3 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <AssignmentReturnIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2, opacity: 0.7 }} />
            <Typography variant="h5" gutterBottom>Access Denied</Typography>
            <Typography variant="body1" color="text.secondary">
              You don't have permission to view return requests. Please contact your administrator.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  const handleRejectCancel = () => {
    setRejectDialogOpen(false);
  };

  const handleComplete = async () => {
    if (!selectedRequest?.id) return;
    try {
      setCurrentAction('complete');
      setActionLoading(true);

      await complete.mutateAsync({ requestId: selectedRequest.id, adminNotes });

      const stockErrors: string[] = [];
      for (const item of selectedRequest.items) {
        try {
          const product = await ProductService.getProduct(item.productId);
          if (item.variantId) {
            const variant = product?.variants?.find((v: any) => v.id === item.variantId);
            const allocations = variant?.warehouseAllocations?.length
              ? variant.warehouseAllocations
              : (product?.warehouseAllocations ?? []);
            for (const alloc of allocations) {
              await ProductService.updateVariantStock(item.productId, item.variantId, item.quantity, 'add', alloc.warehouseId);
            }
          } else {
            const allocations = product?.warehouseAllocations ?? [];
            for (const alloc of allocations) {
              await ProductService.updateProductStock(item.productId, item.quantity, 'add', alloc.warehouseId);
            }
          }
        } catch (stockErr: any) {
          stockErrors.push(`${item.productName}: ${stockErr.message || 'stock update failed'}`);
        }
      }

      if (selectedRequest.orderId) {
        try {
          await OrderService.updateOrderStatus(selectedRequest.orderId, 'returned');
        } catch (orderErr: any) {
          console.error('Failed to update order status:', orderErr);
          showToast('Return completed but order status update failed — please update manually', 'warning');
        }
      }

      if (stockErrors.length > 0) {
        showToast(`Completed with warnings — some stock not restored: ${stockErrors.join('; ')}`, 'warning');
      } else {
        showToast('Return completed — stock restored and order updated', 'success');
      }

      closeDrawer();
      resetToFirstPage();
    } catch (err: any) {
      showToast(`Failed to complete: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setActionLoading(false);
      setCurrentAction(null);
    }
  };

  const handleSaveNotes = async () => {
    if (!selectedRequest?.id) return;
    try {
      setCurrentAction('notes');
      setActionLoading(true);
      await saveNotes.mutateAsync({ requestId: selectedRequest.id, adminNotes });
      showToast('Admin notes saved', 'success');
    } catch (err: any) {
      showToast(`Failed to save notes: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setActionLoading(false);
      setCurrentAction(null);
    }
  };

  const handleSaveRefund = async () => {
    if (!selectedRequest?.id) return;

    const amount = parseFloat(refundedAmount);
    if (isNaN(amount) || amount < 0) {
      showToast('Please enter a valid refunded amount', 'error');
      return;
    }
    if (refundStatus === 'partially_refunded' && amount >= selectedRequest.refundAmount) {
      showToast('For a partial refund the amount must be less than the full eligible amount', 'error');
      return;
    }
    if (refundStatus === 'partially_refunded' && amount <= 0) {
      showToast('Partial refund amount must be greater than zero', 'error');
      return;
    }
    if (refundStatus === 'refunded' && amount <= 0) {
      showToast('Refunded amount must be greater than zero', 'error');
      return;
    }

    try {
      setRefundLoading(true);

      const parsedDate = refundDate ? new Date(refundDate) : undefined;

      await saveRefund.mutateAsync({
        requestId: selectedRequest.id,
        details: {
          refundStatus,
          refundedAmount: amount,
          refundMethod,
          refundTransactionId: refundTransactionId || undefined,
          refundDate: parsedDate,
          refundNotes: refundNotes || undefined,
        },
      });

      if (selectedRequest.orderId) {
        try {
          await OrderService.updateOrderRefundInfo(selectedRequest.orderId, {
            refundStatus,
            refundedAmount: amount,
            refundMethod,
          });
        } catch (orderErr) {
          console.error('Could not update order refund info:', orderErr);
        }
      }

      setSelectedRequest(prev =>
        prev
          ? {
            ...prev,
            refundStatus,
            refundedAmount: amount,
            refundMethod,
            refundTransactionId: refundTransactionId || undefined,
            refundDate: parsedDate,
            refundNotes: refundNotes || undefined,
          }
          : prev
      );

      showToast('Refund details saved', 'success');
      setRefundEditMode(false);
      resetToFirstPage();
    } catch (err: any) {
      showToast(`Failed to save refund: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setRefundLoading(false);
    }
  };

  const handleCancelRefundEdit = () => {
    if (!selectedRequest) return;
    setRefundStatus(selectedRequest.refundStatus || 'pending');
    setRefundedAmount(
      selectedRequest.refundedAmount !== undefined
        ? String(selectedRequest.refundedAmount)
        : String(selectedRequest.refundAmount)
    );
    setRefundMethod(selectedRequest.refundMethod || 'original_payment');
    setRefundTransactionId(selectedRequest.refundTransactionId || '');
    setRefundDate(
      selectedRequest.refundDate
        ? selectedRequest.refundDate.toISOString().split('T')[0]
        : ''
    );
    setRefundNotes(selectedRequest.refundNotes || '');
    setRefundEditMode(false);
  };

  // ── Delete handlers ────────────────────────────────────────────────────────
  const handleDeleteRequest = (request: ReturnRequest) => {
    setDeleteTarget(request);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoadingId(deleteTarget.id);
      const result = await remove.mutateAsync(deleteTarget.id);
      if (result.success) {
        // If the deleted request happened to be open in the drawer, close it
        if (selectedRequest?.id === deleteTarget.id) {
          closeDrawer();
        }
        setDeleteTarget(null);
        resetToFirstPage();
        showToast('Return request deleted successfully', 'success');
      } else {
        showToast(result.message || 'Failed to delete return request', 'error');
      }
    } catch (err: any) {
      showToast(`Failed to delete: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoadingId('');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  // ── Initial full-page loading state ───────────────────────────────────────
  if (loading && requests.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading return requests...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: { md: '100%' },
        display: 'flex',
        flexDirection: 'column',
        overflow: { xs: 'visible', md: 'hidden' },
        p: { xs: 2, sm: 3 },
        gap: 0,
      }}
    >
      {/* ── Fixed Header ── */}
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
          sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AssignmentReturnIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Return Requests
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage customer return and refund requests
              </Typography>
            </Box>
          </Stack>
          <Button
            onClick={handleRefresh}
            variant="outlined"
            startIcon={<RefreshIcon />}
            sx={{ alignSelf: { xs: 'flex-start', sm: 'auto' } }}
            disabled={loading}
          >
            Refresh
          </Button>
        </Stack>

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1, sm: 1.5, md: 2 }} sx={{ mb: 2 }} alignItems="stretch">
          {[
            { label: 'Pending Review', value: allStats.pending, sub: 'Awaiting admin action' },
            { label: 'Approved', value: allStats.approved, sub: 'Awaiting stock restore' },
            { label: 'Completed', value: allStats.completed, sub: 'Fully resolved' },
            { label: 'Rejected', value: allStats.rejected, sub: `${allStats.rejected} denied` },
          ].map(card => (
            <Grid key={card.label} item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
              <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ p: { xs: 1, sm: 1.25 }, '&:last-child': { pb: { xs: 1, sm: 1.25 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  <Typography color="text.secondary" gutterBottom variant="caption" noWrap>
                    {card.label}
                  </Typography>
                  {statsLoading ? (
                    <CircularProgress size={24} sx={{ my: 0.5 }} />
                  ) : (
                    <Typography variant="h5" component="div" color="text.primary"
                      sx={{ fontSize: { xs: '1rem', sm: '1.125rem', md: '1.375rem' }, mb: 0.5 }}>
                      {card.value}
                    </Typography>
                  )}
                  <Box sx={{ mt: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" noWrap>{card.sub}</Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
          >
            <TextField
              placeholder="Search by order #, customer name or email… (Enter)"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 440 } }}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchIcon /></InputAdornment>
                ),
              }}
            />
            <Stack direction="row" alignItems="center" spacing={1} sx={{ flexShrink: 0 }}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select value={statusFilter} onChange={e => handleStatusFilterChange(e.target.value)} label="Status">
                      <MenuItem value="all">All Statuses</MenuItem>
                      <MenuItem value="pending">Pending Review</MenuItem>
                      <MenuItem value="approved">Approved</MenuItem>
                      <MenuItem value="rejected">Rejected</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Reason</InputLabel>
                    <Select value={reasonFilter} onChange={e => handleReasonFilterChange(e.target.value)} label="Reason">
                      <MenuItem value="all">All Reasons</MenuItem>
                      {Object.entries(RETURN_REASON_LABELS).map(([key, label]) => (
                        <MenuItem key={key} value={key}>{label}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${requests.length} matching`
                        : `Showing ${requests.length} on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box
        sx={{
          flex: 1,
          overflow: { xs: 'visible', md: 'auto' },
          display: 'flex',
          flexDirection: 'column',
          minHeight: { xs: 'auto', md: 0 },
        }}
      >
        {requests.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <AssignmentReturnIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Requests Match Your Filters' : 'No Return Requests'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'There are no return requests yet.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
                    Clear Filters
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {requests.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1,
                overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' },
                mb: 2,
                position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 700, md: 1050 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Order #</TableCell>
                    <TableCell>Customer</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Reason</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Items</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Refund Amount</TableCell>
                    <TableCell>Return Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Refund Status</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Submitted</TableCell>
                    {(canView || canManage) && (
                      <TableCell align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requests.map(request => (
                    <TableRow key={request.id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ReceiptIcon fontSize="small" color="action" />
                          <Typography variant="body2" fontWeight={600}>#{request.orderNumber}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {request.customerName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
                          {request.customerEmail}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Chip
                          label={RETURN_REASON_LABELS[request.reason] ?? request.reason}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx('secondary', isDark)}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" color="text.secondary">
                          {request.items.length} item{request.items.length !== 1 ? 's' : ''}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Stack spacing={0.25}>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            {formatCurrency(request.refundAmount)}
                          </Typography>
                          {request.refundedAmount !== undefined &&
                            request.refundedAmount !== request.refundAmount && (
                              <Typography variant="caption" color="text.secondary">
                                Refunded: {formatCurrency(request.refundedAmount)}
                              </Typography>
                            )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={statusIcon(request.status) ?? undefined}
                          label={RETURN_STATUS_LABELS[request.status]}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(statusColor(request.status), isDark)}
                        />
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {request.refundStatus ? (
                          <Chip
                            label={REFUND_STATUS_LABELS[request.refundStatus]}
                            size="small"
                            variant="outlined"
                            sx={getSoftChipSx(refundStatusColor(request.refundStatus), isDark)}
                          />
                        ) : (
                          <Typography variant="caption" color="text.disabled">—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(request.createdAt)}
                        </Typography>
                      </TableCell>
                      {(canView || canManage) && (
                        <TableCell align="right">
                          {/* ── Row actions: View + Delete ── */}
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                            <Tooltip title="View & Manage">
                              <IconButton onClick={() => openDrawer(request)} size="small" color="primary">
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canManage && (
                              <Tooltip title="Delete">
                                <IconButton
                                  onClick={() => handleDeleteRequest(request)}
                                  size="small"
                                  color="error"
                                  disabled={deleteLoadingId === request.id}
                                >
                                  {deleteLoadingId === request.id
                                    ? <CircularProgress size={16} />
                                    : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing{' '}
                    {totalRequests === 0 ? 0 : (currentPage - 1) * REQUESTS_PER_PAGE + 1}
                    –{Math.min(currentPage * REQUESTS_PER_PAGE, totalRequests)} of {totalRequests}
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton
                    showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {/* ── Detail Drawer ── */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={closeDrawer}
        PaperProps={{ sx: { width: { xs: '100%', sm: 580, md: 640 } } }}
      >
        {selectedRequest && (
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

            <Box
              sx={{
                px: 3, py: 2.5,
                borderBottom: 1, borderColor: 'divider',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                flexShrink: 0,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>Return Request Details</Typography>
                <Typography variant="caption" color="text.secondary">Order #{selectedRequest.orderNumber}</Typography>
              </Box>
              {/* ── Drawer header: close + delete ── */}
              <Stack direction="row" spacing={0.5} alignItems="center">
                {canManage && (
                  <Tooltip title="Delete this request">
                    <IconButton
                      onClick={() => handleDeleteRequest(selectedRequest)}
                      size="small"
                      color="error"
                      disabled={actionLoading || refundLoading}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
                <IconButton
                  onClick={closeDrawer}
                  edge="end"
                  size="small"
                  disabled={actionLoading || refundLoading}
                >
                  <CloseIcon />
                </IconButton>
              </Stack>
            </Box>

            <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 3 }}>
              <Stack spacing={2.5}>

                {/* Order Information */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <DrawerSectionHeader icon={<ReceiptIcon fontSize="small" />} title="Order Information" />
                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2, gap: 1 }}>
                      <Chip
                        icon={statusIcon(selectedRequest.status) ?? undefined}
                        label={RETURN_STATUS_LABELS[selectedRequest.status]}
                        size="small"
                        variant="outlined"
                        sx={getSoftChipSx(statusColor(selectedRequest.status), isDark)}
                      />
                      {selectedRequest.refundStatus && (
                        <Chip
                          icon={<PaymentsIcon fontSize="small" />}
                          label={REFUND_STATUS_LABELS[selectedRequest.refundStatus]}
                          size="small"
                          variant="outlined"
                          sx={getSoftChipSx(refundStatusColor(selectedRequest.refundStatus), isDark)}
                        />
                      )}
                    </Stack>
                    <Divider sx={{ mb: 2 }} />
                    <Grid container spacing={0}>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Order Number"
                          value={`#${selectedRequest.orderNumber}`}
                          icon={<ReceiptIcon sx={{ fontSize: 16 }} />}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Eligible Refund"
                          value={
                            <Typography variant="body2" fontWeight={700} color="success.main" component="span">
                              {formatCurrency(selectedRequest.refundAmount)}
                            </Typography>
                          }
                          icon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Submitted"
                          value={formatDate(selectedRequest.createdAt)}
                          icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <InfoRow
                          label="Last Updated"
                          value={formatDate(selectedRequest.updatedAt)}
                          icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {/* Customer */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <DrawerSectionHeader icon={<PersonIcon fontSize="small" />} title="Customer" />
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <Avatar sx={{ width: 44, height: 44, bgcolor: 'primary.main', fontSize: '1rem', fontWeight: 700, flexShrink: 0 }}>
                        {selectedRequest.customerName.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} color="text.primary">
                          {selectedRequest.customerName}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                          >
                            {selectedRequest.customerEmail}
                          </Typography>
                        </Stack>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Return Reason */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <DrawerSectionHeader icon={<LocalOfferIcon fontSize="small" />} title="Return Reason" />
                    <Chip
                      label={RETURN_REASON_LABELS[selectedRequest.reason] ?? selectedRequest.reason}
                      size="small"
                      variant="outlined"
                      sx={getSoftChipSx('secondary', isDark)}
                    />
                    {selectedRequest.damageNotes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block"
                          sx={{ mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Damage / Defect Notes
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                            {selectedRequest.damageNotes}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                    {selectedRequest.additionalNotes && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} display="block"
                          sx={{ mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          Additional Notes
                        </Typography>
                        <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1.5, border: '1px solid', borderColor: 'divider' }}>
                          <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.6 }}>
                            {selectedRequest.additionalNotes}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </CardContent>
                </Card>

                {/* Return Items */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <DrawerSectionHeader
                      icon={<InventoryIcon fontSize="small" />}
                      title={`Return Items (${selectedRequest.items.length})`}
                    />
                    <Stack spacing={0} divider={<Divider />}>
                      {selectedRequest.items.map((item, index) => (
                        <Box key={index} sx={{ py: 1.5 }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            {item.productImage ? (
                              <Avatar src={item.productImage} variant="rounded"
                                sx={{ width: 48, height: 48, flexShrink: 0 }} />
                            ) : (
                              <Avatar variant="rounded"
                                sx={{ width: 48, height: 48, bgcolor: 'action.hover', flexShrink: 0 }}>
                                <InventoryIcon color="action" />
                              </Avatar>
                            )}
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={600} noWrap>{item.productName}</Typography>
                              {item.productSku && (
                                <Typography variant="caption" color="text.secondary" display="block">
                                  SKU: {item.productSku}
                                </Typography>
                              )}
                              {item.variantSelections && item.variantSelections.length > 0 && (
                                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                  {item.variantSelections.map((v, i) => (
                                    <Chip key={i} label={`${v.variantType}: ${v.variantValue}`}
                                      size="small" sx={{ fontSize: '0.65rem', height: 18 }} />
                                  ))}
                                </Stack>
                              )}
                              <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Qty: <strong>{item.quantity}</strong>
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Unit: <strong>{formatCurrency(item.unitPrice)}</strong>
                                </Typography>
                                <Typography variant="caption" color="success.main" fontWeight={700}>
                                  {formatCurrency(item.totalPrice)}
                                </Typography>
                              </Stack>
                            </Box>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ pt: 0.5 }}>
                      <Typography variant="body2" fontWeight={600}>Total Eligible Refund</Typography>
                      <Typography variant="body1" fontWeight={700} color="success.main">
                        {formatCurrency(selectedRequest.refundAmount)}
                      </Typography>
                    </Stack>
                  </CardContent>
                </Card>

                {/* Refund Payment */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <DrawerSectionHeader
                      icon={<PaymentsIcon fontSize="small" />}
                      title="Refund Payment"
                      action={
                        !refundEditMode && canManage ? (
                          <Button size="small" variant="outlined" startIcon={<EditIcon fontSize="small" />}
                            onClick={() => setRefundEditMode(true)} sx={{ minWidth: 80 }}>
                            Edit
                          </Button>
                        ) : null
                      }
                    />

                    {!refundEditMode && (
                      <>
                        <Box sx={{ mb: 2 }}>
                          <Chip
                            icon={<PaymentsIcon fontSize="small" />}
                            label={REFUND_STATUS_LABELS[selectedRequest.refundStatus || 'pending']}
                            size="small"
                            variant="outlined"
                            sx={getSoftChipSx(refundStatusColor(selectedRequest.refundStatus), isDark)}
                          />
                        </Box>
                        {selectedRequest.refundStatus && selectedRequest.refundStatus !== 'pending' ? (
                          <Stack spacing={0}>
                            <InfoRow
                              label="Amount Refunded"
                              value={
                                <Typography variant="body2" fontWeight={700} color="success.main" component="span">
                                  {formatCurrency(selectedRequest.refundedAmount ?? 0)}
                                  <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    of {formatCurrency(selectedRequest.refundAmount)}
                                  </Typography>
                                </Typography>
                              }
                              icon={<PaymentsIcon sx={{ fontSize: 16 }} />}
                            />
                            <InfoRow
                              label="Refund Method"
                              value={selectedRequest.refundMethod ? REFUND_METHOD_LABELS[selectedRequest.refundMethod] : '—'}
                              icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                            />
                            {selectedRequest.refundDate && (
                              <InfoRow
                                label="Refund Date"
                                value={formatDate(selectedRequest.refundDate)}
                                icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                              />
                            )}
                            {selectedRequest.refundTransactionId && (
                              <InfoRow
                                label="Transaction / Ref ID"
                                value={selectedRequest.refundTransactionId}
                                icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                              />
                            )}
                            {selectedRequest.refundNotes && (
                              <InfoRow
                                label="Notes"
                                value={selectedRequest.refundNotes}
                                icon={<NotesIcon sx={{ fontSize: 16 }} />}
                              />
                            )}
                          </Stack>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No refund recorded yet. Click <strong>Edit</strong> to enter refund details.
                          </Typography>
                        )}
                      </>
                    )}

                    {refundEditMode && (
                      <Stack spacing={2}>
                        {selectedRequest.refundStatus && selectedRequest.refundStatus !== 'pending' && (
                          <Alert
                            severity={
                              selectedRequest.refundStatus === 'refunded' ? 'success' :
                                selectedRequest.refundStatus === 'partially_refunded' ? 'warning' : 'info'
                            }
                            sx={{ py: 0.75 }}
                          >
                            <Typography variant="body2">
                              {selectedRequest.refundStatus === 'partially_refunded'
                                ? <>Previously refunded: <strong>{formatCurrency(selectedRequest.refundedAmount ?? 0)}</strong> of {formatCurrency(selectedRequest.refundAmount)}</>
                                : <>Full refund of <strong>{formatCurrency(selectedRequest.refundedAmount ?? selectedRequest.refundAmount)}</strong> previously issued</>
                              }
                            </Typography>
                          </Alert>
                        )}

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Refund Status</InputLabel>
                              <Select
                                value={refundStatus}
                                onChange={e => {
                                  const v = e.target.value as RefundStatus;
                                  setRefundStatus(v);
                                  if (v === 'refunded') setRefundedAmount(String(selectedRequest.refundAmount));
                                  if (v === 'pending') setRefundedAmount('0');
                                }}
                                label="Refund Status"
                              >
                                {(Object.keys(REFUND_STATUS_LABELS) as RefundStatus[]).map(k => (
                                  <MenuItem key={k} value={k}>{REFUND_STATUS_LABELS[k]}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth size="small"
                              label="Amount Refunded"
                              type="number"
                              value={refundedAmount}
                              onChange={e => setRefundedAmount(e.target.value)}
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">{currencySymbol}</InputAdornment>,
                              }}
                              helperText={`Max eligible: ${formatCurrency(selectedRequest.refundAmount)}`}
                              disabled={refundStatus === 'pending'}
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <FormControl fullWidth size="small">
                              <InputLabel>Refund Method</InputLabel>
                              <Select
                                value={refundMethod}
                                onChange={e => setRefundMethod(e.target.value as RefundMethod)}
                                label="Refund Method"
                                disabled={refundStatus === 'pending'}
                              >
                                {(Object.keys(REFUND_METHOD_LABELS) as RefundMethod[]).map(k => (
                                  <MenuItem key={k} value={k}>{REFUND_METHOD_LABELS[k]}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <TextField
                              fullWidth size="small"
                              label="Refund Date"
                              type="date"
                              value={refundDate}
                              onChange={e => setRefundDate(e.target.value)}
                              InputLabelProps={{ shrink: true }}
                              disabled={refundStatus === 'pending'}
                            />
                          </Grid>
                        </Grid>

                        <TextField
                          fullWidth size="small"
                          label="Transaction / Reference ID"
                          value={refundTransactionId}
                          onChange={e => setRefundTransactionId(e.target.value)}
                          placeholder="Bank ref, gateway ID, etc."
                          disabled={refundStatus === 'pending'}
                        />

                        <TextField
                          fullWidth size="small"
                          label="Refund Notes"
                          value={refundNotes}
                          onChange={e => setRefundNotes(e.target.value)}
                          multiline rows={2}
                          placeholder="Additional notes about this refund…"
                        />

                        <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                          <Button size="small" variant="outlined" onClick={handleCancelRefundEdit} disabled={refundLoading}>
                            Cancel
                          </Button>
                          {canManage && (
                            <Button
                              onClick={handleSaveRefund}
                              variant="contained"
                              size="small"
                              disabled={refundLoading}
                              startIcon={<SaveIcon />}
                            >
                              Save Refund
                            </Button>
                          )}
                        </Stack>
                      </Stack>
                    )}
                  </CardContent>
                </Card>

                {/* Admin Notes */}
                <Card variant="outlined">
                  <CardContent sx={{ pb: '16px !important' }}>
                    <DrawerSectionHeader icon={<NotesIcon fontSize="small" />} title="Admin Notes" />
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      placeholder="Add internal notes about this return request…"
                      value={adminNotes}
                      onChange={e => setAdminNotes(e.target.value)}
                      size="small"
                      disabled={actionLoading || !canManage}
                    />
                    <Button
                      onClick={handleSaveNotes}
                      disabled={actionLoading}
                      variant="text"
                      size="small"
                      sx={{ mt: 1 }}
                      startIcon={<SaveIcon />}
                    >
                      Save Notes
                    </Button>
                  </CardContent>
                </Card>

              </Stack>
            </Box>

            {/* Sticky action footer */}
            <Divider />
            <Box sx={{ px: 3, py: 2.5, flexShrink: 0 }}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Current Status: <strong>{RETURN_STATUS_LABELS[selectedRequest.status]}</strong>
              </Typography>

              {canManage && selectedRequest.status === 'pending' && (
                <Stack direction="row" spacing={1.5}>
                  <Button
                    onClick={handleApprove}
                    variant="contained"
                    color="success"
                    disabled={actionLoading}
                    startIcon={<CheckCircleIcon />}
                    fullWidth
                  >
                    Approve
                  </Button>
                  <Button
                    onClick={handleReject}
                    variant="outlined"
                    color="error"
                    disabled={actionLoading}
                    startIcon={<CancelIcon />}
                    fullWidth
                  >
                    Reject
                  </Button>
                </Stack>
              )}

              {canManage && selectedRequest.status === 'approved' && (
                <>
                  <Alert severity="info" sx={{ mb: 1.5, py: 0.5 }}>
                    Completing this return will <strong>restore stock</strong> for all returned items and update the order status.
                  </Alert>
                  <Button
                    onClick={handleComplete}
                    variant="contained"
                    color="primary"
                    disabled={actionLoading}
                    startIcon={<TaskAltIcon />}
                    fullWidth
                  >
                    Mark as Completed
                  </Button>
                </>
              )}

              {(selectedRequest.status === 'completed' || selectedRequest.status === 'rejected') && (
                <Alert severity={selectedRequest.status === 'completed' ? 'success' : 'error'} sx={{ py: 0.5 }}>
                  This request has been{' '}
                  <strong>{selectedRequest.status === 'completed' ? 'completed' : 'rejected'}</strong> and is closed.
                  {selectedRequest.status === 'completed' && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      Stock restored · Order status updated
                    </Typography>
                  )}
                </Alert>
              )}

              <Button onClick={closeDrawer} variant="text" fullWidth sx={{ mt: 1 }} startIcon={<CloseIcon />} disabled={actionLoading}>
                Close
              </Button>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* ── Reject Confirmation Dialog ── */}
      <Dialog open={rejectDialogOpen} onClose={handleRejectCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Return Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reject this return request
            {selectedRequest ? (
              <> for order <strong>#{selectedRequest.orderNumber}</strong></>
            ) : ''}?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleRejectCancel} disabled={rejectDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectConfirm}
            disabled={rejectDialogLoading}
            startIcon={rejectDialogLoading ? <CircularProgress size={16} /> : <CancelIcon />}
          >
            {rejectDialogLoading ? 'Rejecting…' : 'Reject'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ── */}
      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Return Request</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete the return request
            {deleteTarget ? (
              <> for order <strong>#{deleteTarget.orderNumber}</strong></>
            ) : ''}?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleteDialogLoading ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Full-screen action overlay (approve / reject / complete / notes) ── */}
      <Backdrop
        open={actionLoading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              {getActionLabel(currentAction)}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      {/* ── Full-screen refund saving overlay ── */}
      <Backdrop
        open={refundLoading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Saving Refund Details…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      {/* ── Full-screen delete overlay ── */}
      <Backdrop
        open={deleteDialogLoading}
        sx={{ zIndex: (t) => t.zIndex.drawer + 200, flexDirection: 'column', gap: 3, bgcolor: 'rgba(0,0,0,0.6)' }}
      >
        <Box
          sx={{
            bgcolor: 'background.paper',
            borderRadius: 3,
            px: 5,
            py: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2.5,
            minWidth: 280,
            boxShadow: 24,
          }}
        >
          <CircularProgress size={48} thickness={4} />
          <Box sx={{ width: '100%' }}>
            <LinearProgress sx={{ borderRadius: 1, mb: 1.5 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, textAlign: 'center', color: 'text.primary' }}>
              Deleting Return Request…
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
              Please wait, do not close this window
            </Typography>
          </Box>
        </Box>
      </Backdrop>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default ReturnManagement;